import { type Infer, v } from 'convex/values'

import { type LifeCategory, categoryFromText } from './habitCategory'

// Number habits: a habit with a `measure` is logged in amounts (kcal, glasses,
// steps…) instead of checked off. Each logical day's entries sum to a total (or,
// for 'max' habits, keep the best one), and the day counts as done when that
// amount meets the target (see isMeasureMet).

export const measureDirectionValidator = v.union(v.literal('atLeast'), v.literal('atMost'))
// How a day's entries combine: 'sum' (default) adds them up, 'max' keeps the best
// single entry (lifts, plank holds). 'max' is always atLeast.
export const measureAggregateValidator = v.union(v.literal('sum'), v.literal('max'))

export const measureValidator = v.object({
  target: v.number(),
  unit: v.string(),
  direction: measureDirectionValidator,
  quickAdds: v.array(v.number()),
  aggregate: v.optional(measureAggregateValidator),
  // Fasting: hours logged by a start/end timer (see fasting.ts). Always atLeast + max.
  fast: v.optional(v.boolean()),
  // Minutes habits: true adds a start/stop timer, false switches the default off
  // (unset = on when the unit is minutes, see isTimerMeasure).
  timer: v.optional(v.boolean()),
})

export type HabitMeasure = Infer<typeof measureValidator>
export type MeasureDirection = HabitMeasure['direction']
export type MeasureAggregate = NonNullable<HabitMeasure['aggregate']>

export const isBestOfDay = (m: { aggregate?: MeasureAggregate } | null | undefined) =>
  m?.aggregate === 'max'

export const isFast = (m: { fast?: boolean } | null | undefined) => !!m?.fast

const MINUTE_UNIT = /^(min|mins|minute|minutes|minuto|minutos|minuten|分|分钟|分鐘)$/i

// Minutes habits get a timer unless switched off; `timer: true` opts any 'at least' habit in.
export const isTimerMeasure = (
  m: { fast?: boolean; timer?: boolean; unit: string; direction: string } | null | undefined,
) => !!m && !m.fast && m.direction === 'atLeast' && (m.timer ?? MINUTE_UNIT.test(m.unit.trim()))

// Longest timer run that logs (a forgotten timer is capped).
export const MAX_TIMER_MINUTES = 12 * 60
// A stop sooner than this discards the run instead of logging a minute.
export const MIN_TIMER_MS = 30_000

// A day's amount from its entries.
export function dayAmount(amounts: readonly number[], aggregate?: MeasureAggregate): number {
  if (aggregate === 'max') return amounts.length ? Math.max(...amounts) : 0
  return amounts.reduce((n, a) => n + a, 0)
}

// Where an entry came from. Wearables may write entries later.
export const logSourceValidator = v.union(
  v.literal('manual'),
  v.literal('coach'),
  v.literal('photo'),
  v.literal('fitbit'),
  v.literal('strava'),
  // Filled in by ticking the habit off (see completeFor); undoing the tick removes it.
  v.literal('check'),
)

export const MAX_AMOUNT = 1_000_000
export const MAX_QUICK_ADDS = 4
export const MAX_UNIT_LENGTH = 16
export const MAX_NOTE_LENGTH = 200

// Longest fast the timer tracks (and the largest fasting target).
export const MAX_FAST_HOURS = 14 * 24

export const isValidAmount = (n: number) => Number.isFinite(n) && n > 0 && n <= MAX_AMOUNT

// Returns a cleaned measure, or an error string.
export function cleanMeasure(m: HabitMeasure): HabitMeasure | string {
  const unit = m.unit.trim().slice(0, MAX_UNIT_LENGTH)
  if (!isValidAmount(m.target)) return 'Target must be a positive number'
  const quickAdds = [...new Set(m.quickAdds.filter(isValidAmount).map(roundAmount))]
    .sort((a, b) => a - b)
    .slice(0, MAX_QUICK_ADDS)
  const max = m.aggregate === 'max' || !!m.fast
  if (m.fast && m.target > MAX_FAST_HOURS) return 'Target must be a positive number'
  return {
    target: roundAmount(m.target),
    unit,
    direction: max ? 'atLeast' : m.direction,
    quickAdds: quickAdds.length ? quickAdds : [m.fast ? roundAmount(m.target) : 1],
    ...(max && { aggregate: 'max' as const }),
    ...(m.fast && { fast: true }),
    ...(!m.fast && m.timer !== undefined && { timer: m.timer }),
  }
}

export const roundAmount = (n: number) => Math.round(n * 100) / 100

// atMost needs at least one entry: an empty day isn't "under target", it's unlogged.
export function isMeasureMet(m: HabitMeasure, total: number, count: number): boolean {
  return m.direction === 'atLeast' ? total >= m.target : count > 0 && total <= m.target
}

// Small whole-number targets logged one at a time render as segments and a +1 button.
export function isStepper(m: HabitMeasure): boolean {
  return (
    Number.isInteger(m.target) && m.target <= 12 && m.quickAdds.length === 1 && m.quickAdds[0] === 1
  )
}

export function formatAmount(n: number, locale?: string): string {
  return roundAmount(n).toLocaleString(locale, { maximumFractionDigits: 2 })
}

// One line for the coach / MCP: "620 / 2000 kcal today (1380 left, aim: at most)".
// For a 'max' habit `total` is the day's best entry.
export function measureProgressText(m: HabitMeasure, total: number, count: number): string {
  const best = isBestOfDay(m)
  const amt =
    `${best ? 'best ' : ''}${formatAmount(total, 'en')} / ${formatAmount(m.target, 'en')} ${m.unit}`.trim()
  const diff = roundAmount(Math.abs(m.target - total))
  const state =
    m.direction === 'atLeast'
      ? total >= m.target
        ? 'target hit'
        : `${formatAmount(diff, 'en')} ${m.unit} to go`
      : total > m.target
        ? `${formatAmount(diff, 'en')} ${m.unit} over the limit`
        : `${formatAmount(diff, 'en')} ${m.unit} left`
  return `${amt} today (${state}; aim: ${m.direction === 'atLeast' ? 'at least' : 'at most'} ${formatAmount(m.target, 'en')}${best ? ' in a single entry, only the best entry counts' : ''}, ${count} entr${count === 1 ? 'y' : 'ies'})`
}

export type MeasurePresetId =
  | 'calories'
  | 'water'
  | 'meals'
  | 'steps'
  | 'pages'
  | 'pushups'
  | 'lift'
  | 'plank'
  | 'meditate'
  | 'readMinutes'
  | 'breathwork'
  | 'cold'

export const MEASURE_PRESETS: ReadonlyArray<{
  id: MeasurePresetId
  icon: string
  category: LifeCategory
  measure: HabitMeasure
  // Worth suggesting a higher target once it's easy (see lib/levelUp).
  progressive: boolean
}> = [
  {
    id: 'calories',
    progressive: false,
    category: 'health',
    icon: 'utensils',
    measure: { direction: 'atMost', target: 2000, unit: 'kcal', quickAdds: [100, 250, 500, 750] },
  },
  {
    id: 'water',
    progressive: false,
    category: 'health',
    icon: 'droplet',
    measure: { direction: 'atLeast', target: 8, unit: 'glasses', quickAdds: [1] },
  },
  {
    id: 'meals',
    progressive: false,
    category: 'health',
    icon: 'utensils',
    measure: { direction: 'atLeast', target: 5, unit: 'meals', quickAdds: [1] },
  },
  {
    id: 'steps',
    progressive: true,
    category: 'health',
    icon: 'footprints',
    measure: { direction: 'atLeast', target: 8000, unit: 'steps', quickAdds: [1000, 2000] },
  },
  {
    id: 'pages',
    progressive: true,
    category: 'mind',
    icon: 'book-open',
    measure: { direction: 'atLeast', target: 20, unit: 'pages', quickAdds: [5, 10] },
  },
  {
    id: 'pushups',
    progressive: true,
    category: 'health',
    icon: 'dumbbell',
    measure: { direction: 'atLeast', target: 50, unit: 'push-ups', quickAdds: [10, 20] },
  },
  {
    id: 'lift',
    progressive: true,
    category: 'health',
    icon: 'dumbbell',
    measure: {
      direction: 'atLeast',
      target: 60,
      unit: 'kg',
      quickAdds: [40, 50, 60],
      aggregate: 'max',
    },
  },
  {
    id: 'plank',
    progressive: true,
    category: 'health',
    icon: 'timer',
    measure: {
      direction: 'atLeast',
      target: 2,
      unit: 'min',
      quickAdds: [1, 2, 3],
      aggregate: 'max',
      timer: true,
    },
  },
  {
    id: 'meditate',
    progressive: true,
    category: 'mind',
    icon: 'timer',
    measure: { direction: 'atLeast', target: 10, unit: 'min', quickAdds: [5, 10], timer: true },
  },
  {
    id: 'readMinutes',
    progressive: true,
    category: 'mind',
    icon: 'book-open',
    measure: { direction: 'atLeast', target: 20, unit: 'min', quickAdds: [10, 20], timer: true },
  },
  {
    id: 'breathwork',
    progressive: true,
    category: 'mind',
    icon: 'wind',
    measure: { direction: 'atLeast', target: 5, unit: 'min', quickAdds: [3, 5], timer: true },
  },
  {
    id: 'cold',
    progressive: true,
    category: 'health',
    icon: 'snowflake',
    measure: { direction: 'atLeast', target: 2, unit: 'min', quickAdds: [1, 2, 3], timer: true },
  },
]

export type FastPresetId = 'fast16' | 'fast18' | 'omad' | 'fast36' | 'fast72'

export const fastMeasure = (hours: number, unit = 'h'): HabitMeasure => ({
  direction: 'atLeast',
  target: hours,
  unit,
  quickAdds: [hours],
  aggregate: 'max',
  fast: true,
})

// Multi-day fasts don't fit a daily schedule, so they default to weekly / monthly.
export const FAST_PRESETS: ReadonlyArray<{
  id: FastPresetId
  hours: number
  period: 'daily' | 'weekly' | 'monthly'
}> = [
  { id: 'fast16', hours: 16, period: 'daily' },
  { id: 'fast18', hours: 18, period: 'daily' },
  { id: 'omad', hours: 23, period: 'daily' },
  { id: 'fast36', hours: 36, period: 'weekly' },
  { id: 'fast72', hours: 72, period: 'monthly' },
]

export const isMultiDayFast = (m: { fast?: boolean; target: number } | null | undefined) =>
  !!m?.fast && m.target >= 24

// Calorie-style units, for finding the habit a meal photo estimate logs into.
export const isCalorieUnit = (unit: string) => /^(k?cal|calories?|kcals)$/i.test(unit.trim())

// Preset units in every locale plus common free-typed ones.
const UNIT_WORDS: Partial<Record<LifeCategory, string>> = {
  health:
    'kcal cal calories calorie 千卡 meals meal comidas refeições repas 食 餐 glasses glass cups cup ml l liters litres oz 杯 vasos copos verres steps 歩 步 pasos passos pas km mi miles push-ups pushups reps flexiones flexões pompes servings kg kgs lb lbs キロ 公斤 seconds sec secs s 秒 segundos secondes',
  mind: 'pages ページ 页 páginas chapters chapter',
  finance: '$ usd eur € £ ¥ yen dollars',
  creative: 'words mots palabras palavras 字',
}
const UNIT_CATEGORY = new Map(
  Object.entries(UNIT_WORDS).flatMap(([cat, units = '']) =>
    units.split(' ').map((u) => [u, cat as LifeCategory] as const),
  ),
)

// Category for a new number habit: its unit first, then keywords in the title.
export function measureCategory(m: HabitMeasure, title: string): LifeCategory | undefined {
  if (m.fast) return 'health'
  return UNIT_CATEGORY.get(m.unit.trim().toLowerCase()) ?? categoryFromText(`${title} ${m.unit}`)
}

const titleKey = (s: string) =>
  s
    .split('///')[0]
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()

// Presets worth suggesting: not already tracked under the same title or unit.
export function presetsToSuggest(
  habits: ReadonlyArray<{ title: string; measure?: { unit: string } | null }>,
  localize: (id: MeasurePresetId) => { title: string; unit: string },
) {
  const titles = new Set(habits.map((h) => titleKey(h.title)))
  const units = new Set(habits.flatMap((h) => (h.measure ? [h.measure.unit.toLowerCase()] : [])))
  return MEASURE_PRESETS.filter((p) => {
    const { title, unit } = localize(p.id)
    // Several timer presets share "min", so only their titles dedupe.
    return !titles.has(titleKey(title)) && (p.measure.timer || !units.has(unit.toLowerCase()))
  })
}
